import React from 'react';
import Layout from '../components/Layout';
import AdminNavBar from '../components/AdminNavBar';

interface List {
  id: number;
  name: string;
  description?: string;
  type?: string;
  optin?: string;
}

interface SubscriberListRef {
  id: number;
  name: string;
}

interface Subscriber {
  id: number;
  name: string | null;
  email: string;
  status: string;
  lists?: SubscriberListRef[];
}

interface SubscriberFormValues {
  name: string;
  email: string;
  status: string;
  listIds: number[];
}

export default function ManageSubscribers() {
  // ----------------------------------------
  // React state
  // ----------------------------------------
  const [lists, setLists] = React.useState<List[]>([]);
  const [subscribers, setSubscribers] = React.useState<Subscriber[]>([]);
  const [loading, setLoading] = React.useState<boolean>(true);
  const [selectedListId, setSelectedListId] = React.useState<number | 'all'>(
    'all'
  );

  // Editing / creating state
  const [editingSubscriber, setEditingSubscriber] =
    React.useState<Subscriber | null>(null);
  const [isCreating, setIsCreating] = React.useState<boolean>(false);
  const [formValues, setFormValues] = React.useState<SubscriberFormValues>({
    name: '',
    email: '',
    status: 'enabled',
    listIds: [],
  });
  const [saving, setSaving] = React.useState(false);
  const [saveError, setSaveError] = React.useState<string | null>(null);
  const [deletingId, setDeletingId] = React.useState<number | null>(null);
  const [showDebug, setShowDebug] = React.useState(false);
  const baseUrl = process.env.REACT_APP_API_URL || '';

  // Derived: subscribers filtered by list
  const filteredSubscribers = React.useMemo(() => {
    if (selectedListId === 'all') return subscribers;

    return subscribers.filter(
      (sub) =>
        Array.isArray(sub.lists) &&
        sub.lists.some((l) => l.id === selectedListId)
    );
  }, [subscribers, selectedListId]);

  // ----------------------------------------
  // Load Listmonk data on component mount
  // ----------------------------------------
  React.useEffect(() => {
    async function loadData() {
      try {
        console.log('ManageSubscribers REACT_APP_API_URL =', baseUrl);

        // ----- Fetch lists -----
        const listsRes = await fetch(`${baseUrl}/api/listmonk/lists`);
        console.log('Lists response status:', listsRes.status);

        if (!listsRes.ok) {
          const text = await listsRes.text();
          console.error('Lists error body:', text);
          throw new Error(`Lists fetch failed: ${listsRes.status}`);
        }

        const listsJson = await listsRes.json();
        console.log('Lists JSON:', listsJson);

        // ----- Fetch subscribers -----
        const subsRes = await fetch(`${baseUrl}/api/listmonk/subscribers`);
        console.log('Subscribers response status:', subsRes.status);

        if (!subsRes.ok) {
          const text = await subsRes.text();
          console.error('Subscribers error body:', text);
          throw new Error(`Subscribers fetch failed: ${subsRes.status}`);
        }

        const subsJson = await subsRes.json();
        console.log('Subscribers JSON:', subsJson);

        // Handle different possible shapes from backend/Listmonk
        const listData: List[] = Array.isArray(listsJson)
          ? listsJson
          : (listsJson.data ||
             listsJson.lists ||
             listsJson.results ||
             []);

        const subscriberData: Subscriber[] = Array.isArray(subsJson)
          ? subsJson
          : (subsJson.data ||
             subsJson.subscribers ||
             subsJson.results ||
             []);

        setLists(listData || []);
        setSubscribers(subscriberData || []);
      } catch (err) {
        console.error('Failed to load subscriber data:', err);
      } finally {
        setLoading(false);
      }
    }

    loadData();
  }, [baseUrl]);

  // ----------------------------------------
  // Editing / creating helpers
  // ----------------------------------------
  function openEdit(sub: Subscriber) {
    setIsCreating(false);
    setEditingSubscriber(sub);
    setSaveError(null);

    setFormValues({
      name: sub.name || '',
      email: sub.email,
      status: sub.status || 'enabled',
      listIds: Array.isArray(sub.lists) ? sub.lists.map((l) => l.id) : [],
    });
  }

  function openCreate() {
    setIsCreating(true);
    setEditingSubscriber(null);
    setSaveError(null);

    setFormValues({
      name: '',
      email: '',
      status: 'enabled',
      listIds: [],
    });
  }

  function closeEdit() {
    setEditingSubscriber(null);
    setIsCreating(false);
    setSaveError(null);
  }

  function toggleListInForm(listId: number) {
    setFormValues((prev) => {
      const already = prev.listIds.includes(listId);
      return {
        ...prev,
        listIds: already
          ? prev.listIds.filter((id) => id !== listId)
          : [...prev.listIds, listId],
      };
    });
  }

  async function handleSave() {
    setSaving(true);
    setSaveError(null);

    try {
      const payload = {
        email: formValues.email,
        name: formValues.name,
        status: formValues.status,
        list_ids: formValues.listIds,
      };

      if (isCreating) {
        // CREATE
        const res = await fetch(`${baseUrl}/api/listmonk/subscribers`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        });

        if (!res.ok) {
          const text = await res.text();
          console.error('Create subscriber error:', res.status, text);
          throw new Error(`Create failed: ${res.status}`);
        }

        const createdJson = await res.json();
        const created: Subscriber =
          (createdJson.subscriber as Subscriber) ||
          (createdJson.data as Subscriber) ||
          (createdJson as Subscriber);

        setSubscribers((prev) => [created, ...prev]);
      } else {
        // UPDATE
        if (!editingSubscriber) return;

        const res = await fetch(
          `${baseUrl}/api/listmonk/subscribers/${editingSubscriber.id}`,
          {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(payload),
          }
        );

        if (!res.ok) {
          const text = await res.text();
          console.error('Save subscriber error:', res.status, text);
          throw new Error(`Save failed: ${res.status}`);
        }

        const updatedJson = await res.json();
        const updated: Subscriber =
          (updatedJson.subscriber as Subscriber) ||
          (updatedJson.data as Subscriber) ||
          (updatedJson as Subscriber);

        setSubscribers((prev) =>
          prev.map((s) => (s.id === updated.id ? updated : s))
        );
      }

      closeEdit();
    } catch (err: any) {
      console.error('Failed to save subscriber:', err);
      setSaveError(err?.message || 'Failed to save subscriber');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(sub: Subscriber) {
    const confirmed = window.confirm(
      `Delete subscriber ${sub.email}? This cannot be undone.`
    );
    if (!confirmed) return;

    setDeletingId(sub.id);
    try {
      const res = await fetch(
        `${baseUrl}/api/listmonk/subscribers/${sub.id}`,
        {
          method: 'DELETE',
        }
      );

      if (!res.ok) {
        const text = await res.text();
        console.error('Delete subscriber error:', res.status, text);
        throw new Error(`Delete failed: ${res.status}`);
      }

      setSubscribers((prev) => prev.filter((s) => s.id !== sub.id));

      // If we were editing this one, close panel
      if (editingSubscriber && editingSubscriber.id === sub.id) {
        closeEdit();
      }
    } catch (err: any) {
      console.error('Failed to delete subscriber:', err);
      // Optional: surface error somewhere if you want
    } finally {
      setDeletingId(null);
    }
  }

  // ----------------------------------------
  // Render
  // ----------------------------------------
  return (
    <Layout>
      <AdminNavBar />

      <div className="pt-[110px] min-h-screen bg-fractal-pattern-3 bg-cover bg-fixed p-10 relative">
        <h1 className="text-4xl font-extrabold text-center text-gray-800 mb-10">
          Manage Subscribers
        </h1>

        <div className="max-w-5xl mx-auto bg-white/80 backdrop-blur-md shadow-xl rounded-xl p-8 border border-gray-200">
          {loading ? (
            <p className="text-center text-gray-600 text-lg">Loading...</p>
          ) : (
            <>
              {/* Summary */}
              <div className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                  <h2 className="text-xl font-bold text-gray-700 mb-2">
                    Overview
                  </h2>

                  <p className="text-gray-700">
                    Subscribers loaded:{' '}
                    <span className="font-semibold">
                      {subscribers.length}
                    </span>
                  </p>

                  <p className="text-gray-700">
                    Lists available:{' '}
                    <span className="font-semibold">{lists.length}</span>
                  </p>
                </div>

                <div className="flex gap-3">
                  <button
                    type="button"
                    className="px-4 py-2 rounded-md bg-green-600 text-white text-sm hover:bg-green-700"
                    onClick={openCreate}
                  >
                    + New subscriber
                  </button>
                </div>
              </div>

            {/* Subscribers table + filter */}
            <div className="mt-6">
              <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between mb-4">
                <div>
                  <h2 className="text-xl font-bold text-gray-800">Subscribers</h2>
                  <p className="text-xs text-gray-500">
                    Manage contacts synced with Listmonk. Use filters to narrow by list.
                  </p>
                </div>

                {/* List filter */}
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600 whitespace-nowrap">
                    Filter by list:
                  </span>
                  <select
                    className="text-sm border border-gray-300 rounded-lg px-3 py-2 bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    value={selectedListId === 'all' ? 'all' : String(selectedListId)}
                    onChange={(e) => {
                      const val = e.target.value;
                      setSelectedListId(val === 'all' ? 'all' : Number(val));
                    }}
                  >
                    <option value="all">All lists</option>
                    {lists.map((list) => (
                      <option key={list.id} value={list.id}>
                        {list.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {filteredSubscribers.length === 0 ? (
                <div className="border border-dashed border-gray-300 rounded-lg p-6 text-center text-sm text-gray-500 bg-gray-50">
                  No subscribers found for this filter.
                </div>
              ) : (
                <div className="overflow-x-auto border border-gray-200 rounded-xl shadow-sm">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr className="text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                        <th className="px-4 py-3">Name</th>
                        <th className="px-4 py-3">Email</th>
                        <th className="px-4 py-3">Status</th>
                        <th className="px-4 py-3">Lists</th>
                        <th className="px-4 py-3 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white">
                      {filteredSubscribers.map((sub, idx) => (
                        <tr
                          key={sub.id}
                          className={`border-t border-gray-100 transition-colors ${
                            idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/60'
                          } hover:bg-emerald-50/60`}
                        >
                          <td className="px-4 py-3 align-top">
                            <div className="font-medium text-gray-800">
                              {sub.name || '—'}
                            </div>
                            <div className="text-[11px] text-gray-400">
                              ID #{sub.id}
                            </div>
                          </td>

                          <td className="px-4 py-3 align-top">
                            <span className="text-gray-800">{sub.email}</span>
                          </td>

                          <td className="px-4 py-3 align-top">
                            {sub.status === 'enabled' && (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-[11px] font-semibold bg-emerald-100 text-emerald-800">
                                ● Enabled
                              </span>
                            )}
                            {sub.status === 'disabled' && (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-[11px] font-semibold bg-gray-200 text-gray-700">
                                ● Disabled
                              </span>
                            )}
                            {sub.status === 'unconfirmed' && (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-[11px] font-semibold bg-amber-100 text-amber-800">
                                ● Unconfirmed
                              </span>
                            )}
                            {sub.status === 'blocklisted' && (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-[11px] font-semibold bg-red-100 text-red-800">
                                ● Blocklisted
                              </span>
                            )}
                          </td>

                          <td className="px-4 py-3 align-top">
                            {Array.isArray(sub.lists) && sub.lists.length > 0 ? (
                              <div className="flex flex-wrap gap-1">
                                {sub.lists.map((l) => (
                                  <span
                                    key={l.id}
                                    className="inline-flex items-center px-2 py-1 rounded-full text-[11px] bg-gray-100 text-gray-700"
                                  >
                                    {l.name}
                                  </span>
                                ))}
                              </div>
                            ) : (
                              <span className="text-xs text-gray-400">No lists</span>
                            )}
                          </td>

                          <td className="px-4 py-3 align-top">
                            <div className="flex justify-end gap-2">
                              <button
                                type="button"
                                className="text-xs px-3 py-1 rounded-full border border-gray-300 text-gray-700 hover:bg-gray-100 transition"
                                onClick={() => openEdit(sub)}
                              >
                                Edit
                              </button>
                              <button
                                type="button"
                                className="text-xs px-3 py-1 rounded-full border border-red-200 text-red-700 hover:bg-red-50 disabled:opacity-60 transition"
                                onClick={() => handleDelete(sub)}
                                disabled={deletingId === sub.id}
                              >
                                {deletingId === sub.id ? 'Deleting…' : 'Delete'}
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>


              {/* Edit / Create panel */}
              {(editingSubscriber || isCreating) && (
                <div className="mt-8 p-4 border border-gray-200 rounded-lg bg-white">
                  <h3 className="text-lg font-semibold text-gray-800 mb-3">
                    {isCreating ? 'New Subscriber' : 'Edit Subscriber'}
                  </h3>

                  {saveError && (
                    <div className="mb-3 text-sm text-red-600">
                      {saveError}
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Name
                      </label>
                      <input
                        type="text"
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                        value={formValues.name}
                        onChange={(e) =>
                          setFormValues((prev) => ({
                            ...prev,
                            name: e.target.value,
                          }))
                        }
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Email
                      </label>
                      <input
                        type="email"
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                        value={formValues.email}
                        onChange={(e) =>
                          setFormValues((prev) => ({
                            ...prev,
                            email: e.target.value,
                          }))
                        }
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Status
                      </label>
                      <select
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                        value={formValues.status}
                        onChange={(e) =>
                          setFormValues((prev) => ({
                            ...prev,
                            status: e.target.value,
                          }))
                        }
                      >
                        <option value="enabled">Enabled</option>
                        <option value="disabled">Disabled</option>
                        <option value="unconfirmed">Unconfirmed</option>
                        <option value="blocklisted">Blocklisted</option>
                      </select>
                    </div>

                    <div>
                      <span className="block text-sm font-medium text-gray-700 mb-1">
                        Lists
                      </span>
                      <div className="max-h-32 overflow-auto border border-gray-200 rounded-md px-2 py-1 text-sm">
                        {lists.map((list) => (
                          <label
                            key={list.id}
                            className="flex items-center gap-2 py-1"
                          >
                            <input
                              type="checkbox"
                              className="h-4 w-4"
                              checked={formValues.listIds.includes(list.id)}
                              onChange={() => toggleListInForm(list.id)}
                            />
                            <span>{list.name}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <button
                      type="button"
                      className="px-4 py-2 rounded-md bg-blue-600 text-white text-sm disabled:opacity-60"
                      onClick={handleSave}
                      disabled={saving}
                    >
                      {saving
                        ? isCreating
                          ? 'Creating…'
                          : 'Saving…'
                        : isCreating
                        ? 'Create subscriber'
                        : 'Save changes'}
                    </button>
                    <button
                      type="button"
                      className="px-4 py-2 rounded-md border border-gray-300 text-sm"
                      onClick={closeEdit}
                      disabled={saving}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {/* TEMPORARY DIAGNOSTIC OUTPUT */}
              <div className="mt-6">
                <button
                  type="button"
                  className="text-xs px-3 py-1 rounded-full border border-gray-300 text-gray-600 bg-white hover:bg-gray-100"
                  onClick={() => setShowDebug((prev) => !prev)}
                >
                  {showDebug ? 'Hide debug JSON' : 'Show debug JSON'}
                </button>

                {showDebug && (
                  <div className="mt-3 bg-gray-100 p-4 rounded-lg text-xs text-gray-800 overflow-auto max-h-96">
                    <h3 className="font-semibold mb-2">Debug Output</h3>
                    <pre>{JSON.stringify({ lists, subscribers }, null, 2)}</pre>
                  </div>
                )}
              </div>

            </>
          )}
        </div>
      </div>
    </Layout>
  );
}
